// Firestore access for the `gyms` collection.
//
// Import direction rule (Standards.md §2.1): contexts deep-import this file,
// never the feature barrel; this module never imports context or components.

import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  deleteDoc,
  updateDoc,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from 'config/firebase';
import { COLLECTIONS } from 'constants/collections';
import { MIME_TO_EXT } from 'utils/image';
import { findUniqueGymSlug } from 'utils/slugify';

// Looks like a Firestore auto-id (20 chars, alphanumeric — case-sensitive).
const isFirestoreId = (s) => typeof s === 'string' && /^[A-Za-z0-9]{20}$/.test(s);

export const getGymById = async (gymId) => {
  const snap = await getDoc(doc(db, COLLECTIONS.GYMS, gymId));
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
};

export const getGymBySlug = async (slug) => {
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.GYMS), where('slug', '==', slug), limit(1))
  );
  return snap.empty ? null : { ...snap.docs[0].data(), id: snap.docs[0].id };
};

export const getAllGyms = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.GYMS));
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
};

// Resolve a gym from a public URL key, preserving the legacy lookup order:
//   1. /gym/:slug          → slug query
//   2. /profile/:id        → direct doc get when id-shaped,
//                            then slug query (old links used slugs here),
//                            then ownerUserIds fallback (legacy event links
//                            pointed at the owner's uid).
// Returns { gym, gymId } or null.
export const resolveGymByKey = async ({ slug = null, idOrSlug = null }) => {
  if (slug) {
    const bySlug = await getGymBySlug(slug);
    if (bySlug) {
      const { id, ...gym } = bySlug;
      return { gym, gymId: id };
    }
  }
  if (idOrSlug) {
    if (isFirestoreId(idOrSlug)) {
      const direct = await getDoc(doc(db, COLLECTIONS.GYMS, idOrSlug));
      if (direct.exists()) return { gym: direct.data(), gymId: direct.id };
    }
    const bySlug = await getGymBySlug(idOrSlug);
    if (bySlug) {
      const { id, ...gym } = bySlug;
      return { gym, gymId: id };
    }
    const ownerSnap = await getDocs(
      query(
        collection(db, COLLECTIONS.GYMS),
        where('ownerUserIds', 'array-contains', idOrSlug),
        limit(1)
      )
    );
    if (!ownerSnap.empty) return { gym: ownerSnap.docs[0].data(), gymId: ownerSnap.docs[0].id };
  }
  return null;
};

// Fetch the gym for an owner/coach/admin user. Prefers the profile's gymIds
// array (set during migration or gym creation); falls back to querying
// ownerUserIds. Returns { gym, gymId } or null.
export const getGymForOwner = async (uid, profile) => {
  if (profile?.gymIds?.length > 0) {
    const gymSnap = await getDoc(doc(db, COLLECTIONS.GYMS, profile.gymIds[0]));
    if (gymSnap.exists()) return { gym: gymSnap.data(), gymId: gymSnap.id };
  }
  const q = query(collection(db, COLLECTIONS.GYMS), where('ownerUserIds', 'array-contains', uid));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { gym: snap.docs[0].data(), gymId: snap.docs[0].id };
};

// Find a slug that isn't already used. Falls back to `${slug}-${suffix}` if
// collision; after 50 attempts, a timestamp suffix.
export const ensureUniqueGymSlug = async (baseSlug, ignoreGymId = null) => {
  if (!baseSlug) return baseSlug;
  let slug = baseSlug;
  let suffix = 1;
  while (true) {
    const snap = await getDocs(query(collection(db, COLLECTIONS.GYMS), where('slug', '==', slug)));
    const collides = snap.docs.some((d) => d.id !== ignoreGymId);
    if (!collides) return slug;
    suffix += 1;
    slug = `${baseSlug}-${suffix}`;
    if (suffix > 50) return `${baseSlug}-${Date.now()}`;
  }
};

// Merge-patch an existing gym; stamps updatedAt.
export const updateGym = async (gymId, patch) => {
  await setDoc(doc(db, COLLECTIONS.GYMS, gymId), patch, { merge: true });
};

// Create a gym doc (caller composes the full document, including approval
// state and ownership); stamps createdAt/updatedAt. Returns the new id.
export const createGym = async (gymData) => {
  const ref = await addDoc(collection(db, COLLECTIONS.GYMS), {
    ...gymData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

// Delete a gym and its owner's events; clears the gym from the user profile.
export const deleteGymCascade = async ({ gymId, userId }) => {
  const eventsSnap = await getDocs(
    query(collection(db, COLLECTIONS.EVENTS), where('gymOwnerId', '==', userId))
  );
  await Promise.all(eventsSnap.docs.map((d) => deleteDoc(d.ref)));
  await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
    gymIds: arrayRemove(gymId),
    gymSetupComplete: false,
  });
  await deleteDoc(doc(db, COLLECTIONS.GYMS, gymId));
};

// ── Storage uploads ──────────────────────────────────────────────────────────

const uploadLogo = async (pathPrefix, uid, file) => {
  const ext = MIME_TO_EXT[file.type] || file.name.split('.').pop() || 'jpg';
  const storageRef = ref(storage, `${pathPrefix}/${uid}/logo_${Date.now()}.${ext}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
};

export const uploadGymLogo = (uid, file) => uploadLogo('gyms', uid, file);
export const uploadCoachLogo = (uid, file) => uploadLogo('coaches', uid, file);

// Gym auto-created from an event post (unclaimed; future events can link to
// it by gymId). Reserves a unique slug; returns the new gym id.
export const createAutoGymFromEvent = async ({
  name,
  city,
  country,
  address,
  lat,
  lng,
  createdByUid,
}) => {
  const newGymRef = doc(collection(db, COLLECTIONS.GYMS));
  const slug = await findUniqueGymSlug(db, { name, city, country });
  await setDoc(newGymRef, {
    name,
    city,
    country,
    address,
    slug,
    lat,
    lng,
    isClaimed: false,
    ownerUserIds: [],
    source: 'auto_from_event',
    createdByUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return newGymRef.id;
};
